import React from 'react';
import {
  DocumentTextIcon,
  TagIcon,
  LinkIcon,
  PencilIcon,
  TrashIcon,
  CloudArrowDownIcon,
} from '@heroicons/react/24/outline';
import {
  Document,
  getDocumentTypeLabel,
  getDocumentTypeColor,
  formatFileSize,
} from '../../api/documents';
import { BrandModal, BrandModalBody, BrandModalFooter, BrandModalHeader } from '../ui/BrandModal';

interface ViewDocumentModalProps {
  isOpen: boolean;
  onClose: () => void;
  document: Document | null;
  onEdit: () => void;
  onDelete: () => void;
}

const ViewDocumentModal: React.FC<ViewDocumentModalProps> = ({
  isOpen,
  onClose,
  document,
  onEdit,
  onDelete,
}) => {
  if (!document) return null;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleDownload = () => {
    if (document.id) {
      // Use the download endpoint
      const downloadUrl = `/api/documents/${document.id}/download`;
      window.open(downloadUrl, '_blank');
    } else if (document.fileUrl) {
      window.open(document.fileUrl, '_blank');
    }
  };

  const handleView = () => {
    if (document.id) {
      // Use the view endpoint
      const viewUrl = `/api/documents/${document.id}/view`;
      window.open(viewUrl, '_blank');
    } else if (document.fileUrl) {
      window.open(document.fileUrl, '_blank');
    }
  };

  return (
    <BrandModal open={isOpen} onClose={onClose} maxWidthClass="max-w-2xl">
      <BrandModalHeader
        title={
          <span className="flex items-center gap-3">
            <DocumentTextIcon className="h-7 w-7 shrink-0 text-slate-400" />
            <span className="min-w-0">{document.title}</span>
          </span>
        }
        subtitle={getDocumentTypeLabel(document.type)}
        onClose={onClose}
      />
      <BrandModalBody>
            <div className="mb-4">
              <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getDocumentTypeColor(document.type)}`}>
                {getDocumentTypeLabel(document.type)}
              </span>
            </div>

            {/* Document Details */}
            <div className="space-y-6">
              {/* Description */}
              {document.description && (
                <div>
                  <h4 className="text-sm font-medium text-slate-700 mb-2">Description</h4>
                  <p className="text-sm text-slate-600">{document.description}</p>
                </div>
              )}

              {/* File Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="text-sm font-medium text-slate-700 mb-2">File Information</h4>
                  <div className="space-y-2 text-sm text-slate-600">
                    {document.name && (
                      <div className="flex justify-between">
                        <span>File Name:</span>
                        <span className="font-medium">{document.name}</span>
                      </div>
                    )}
                    {document.mimeType && (
                      <div className="flex justify-between">
                        <span>Type:</span>
                        <span className="font-medium">{document.mimeType}</span>
                      </div>
                    )}
                    {document.fileSize && (
                      <div className="flex justify-between">
                        <span>Size:</span>
                        <span className="font-medium">{formatFileSize(document.fileSize)}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-slate-700 mb-2">Dates</h4>
                  <div className="space-y-2 text-sm text-slate-600">
                    <div className="flex justify-between">
                      <span>Created:</span>
                      <span className="font-medium">{formatDate(document.createdAt)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Updated:</span>
                      <span className="font-medium">{formatDate(document.updatedAt)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Metadata */}
              {document.metadata && Object.values(document.metadata).some(v => v) && (
                <div>
                  <h4 className="text-sm font-medium text-slate-700 mb-2">Metadata</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-slate-600">
                    {document.metadata.issueDate && (
                      <div className="flex justify-between">
                        <span>Issue Date:</span>
                        <span className="font-medium">
                          {new Date(document.metadata.issueDate).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                    {document.metadata.expiryDate && (
                      <div className="flex justify-between">
                        <span>Expiry Date:</span>
                        <span className="font-medium">
                          {new Date(document.metadata.expiryDate).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                    {document.metadata.issuer && (
                      <div className="flex justify-between">
                        <span>Issuer:</span>
                        <span className="font-medium">{document.metadata.issuer}</span>
                      </div>
                    )}
                    {document.metadata.reference && (
                      <div className="flex justify-between">
                        <span>Reference:</span>
                        <span className="font-medium">{document.metadata.reference}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Tags */}
              {document.tags && document.tags.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-slate-700 mb-2">Tags</h4>
                  <div className="flex flex-wrap gap-1">
                    {document.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-violet-100 text-violet-800"
                      >
                        <TagIcon className="h-3 w-3 mr-1" />
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Associations */}
              {(document.birdId || document.healthTestId) && (
                <div>
                  <h4 className="text-sm font-medium text-slate-700 mb-2">Associations</h4>
                  <div className="space-y-2 text-sm text-slate-600">
                    {document.birdId && (
                      <div className="flex justify-between">
                        <span>Bird ID:</span>
                        <span className="font-medium">{document.birdId}</span>
                      </div>
                    )}
                    {document.healthTestId && (
                      <div className="flex justify-between">
                        <span>Health Test ID:</span>
                        <span className="font-medium">{document.healthTestId}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* File Preview/URL */}
              {document.fileUrl && (
                <div>
                  <h4 className="text-sm font-medium text-slate-700 mb-2">File Preview</h4>
                  <div className="space-y-2">
                    {document.mimeType?.startsWith('image/') && (
                      <div className="border border-gray-200 rounded-md p-2 bg-slate-50/80">
                        <img
                          src={document.fileUrl.startsWith('/') ? `/api${document.fileUrl}` : document.fileUrl}
                          alt={document.title}
                          className="max-w-full h-auto max-h-96 mx-auto rounded"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      </div>
                    )}
                    {document.mimeType === 'application/pdf' && (
                      <div className="border border-gray-200 rounded-md bg-slate-50/80">
                        <iframe
                          src={document.id ? `/api/documents/${document.id}/view` : (document.fileUrl.startsWith('/') ? `/api${document.fileUrl}` : document.fileUrl)}
                          className="w-full h-96 rounded"
                          title={document.title}
                        />
                      </div>
                    )}
                    {document.mimeType?.startsWith('text/') && (
                      <div className="border border-gray-200 rounded-md p-4 bg-slate-50/80 max-h-96 overflow-auto">
                        <pre className="text-sm whitespace-pre-wrap font-mono">
                          {document.fileUrl}
                        </pre>
                      </div>
                    )}
                    <div className="flex items-center space-x-2 pt-2">
                      <LinkIcon className="h-4 w-4 text-slate-400" />
                      <a
                        href={document.fileUrl.startsWith('/') ? `/api${document.fileUrl}` : document.fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-cyan-600 hover:text-cyan-500 text-sm truncate"
                      >
                        {document.fileUrl}
                      </a>
                    </div>
                  </div>
                </div>
              )}
            </div>
      </BrandModalBody>

      <BrandModalFooter>
        <button type="button" onClick={onClose} className="btn btn-secondary text-sm">
          Close
        </button>
        {document.fileUrl && (
          <>
            <button type="button" onClick={handleView} className="btn btn-secondary text-sm">
              <LinkIcon className="h-4 w-4 mr-2" />
              View
            </button>
            <button type="button" onClick={handleDownload} className="btn btn-secondary text-sm">
              <CloudArrowDownIcon className="h-4 w-4 mr-2" />
              Download
            </button>
          </>
        )}
        <button type="button" onClick={onEdit} className="btn btn-primary text-sm">
          <PencilIcon className="h-4 w-4 mr-2" />
          Edit
        </button>
        <button type="button" onClick={onDelete} className="btn btn-danger text-sm">
          <TrashIcon className="h-4 w-4 mr-2" />
          Delete
        </button>
      </BrandModalFooter>
    </BrandModal>
  );
};

export default ViewDocumentModal; 